import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useNavigate, useOutletContext } from 'react-router-dom'
import StockContext from '../../../../contexts/StockContext'
import TableFiltersContext from '../../../../contexts/TableFiltersContext'
import ModalContext from '../../../../contexts/ModalContext'
import SectionContext from '../../../../contexts/SectionContext'
import { FeedbackContext } from '../../../../contexts/FeedbackContext'
import ApiService from '../../../../services/ApiService'
import axios from '../../../../config/axios'
import { useTenant } from '../../../../contexts/TenantContext'
import { Eye, Pencil, Copy, ArrowLeftRight, Power, Check, X } from 'lucide-react'
import { StandardDataTable, useColumnsState } from '../../../../components/StandardPage'
import InfiniteSelect from '../../../../components/InfiniteSelect'
import LoadingComponent from '../../../../components/LoadingComponent'
import { formatReceivedDate } from './utils'
import { StockOutletType } from '..'
import { TypeMainOutlet } from '../..'
import { buildStockColumns, stockScreenKey } from './stockColumns'

const StockTable: React.FC = () => {
  const { setStock, stockList, updateStockList, refresh, hasMoreStock, isLoading } =
    useContext(StockContext)
  const { stockFilters } = useContext(TableFiltersContext)
  const { search } = useOutletContext<TypeMainOutlet>()
  const { setModalActive, setModalSection, setItIsUpdate, setIsVariation, stockMode, setStockMode } =
    useContext(ModalContext)
  const { setFeedback } = useContext(FeedbackContext)
  const { isFashionTenant } = useTenant()
  const isFashion = isFashionTenant || stockMode === 'fashion'
  const { setQuantity, quantity } = useOutletContext<StockOutletType>()
  const { setStockSection } = useContext(SectionContext)
  const navigate = useNavigate()

  const [selectedStockIds, setSelectedStockIds] = useState<Set<number>>(new Set())
  const [bulkDraft, setBulkDraft] = useState<Record<string, string>>({})
  const [isBulkSaving, setIsBulkSaving] = useState<boolean>(false)

  const columns = useMemo(() => buildStockColumns({ isFashion, navigate }), [isFashion, navigate])
  const { columnsState, handleColumnsChange, handleRestoreColumns } = useColumnsState(
    stockScreenKey(isFashion),
    columns,
  )

  useEffect(() => {
    setStockSection('stock')
  }, [setStockSection])

  const [totalStockCount, setTotalStockCount] = useState<number | undefined>(
    undefined,
  )

  useEffect(() => {
    let mounted = true
    const fetchTotal = async () => {
      try {
        const parameters: any = {}
        if (stockFilters && stockFilters.length > 0) parameters.filters = JSON.stringify(stockFilters)
        if (search && search.trim().length > 0) parameters.global = search
        const resp = await ApiService.stock.count(parameters)
        if (mounted && resp && resp.data != null) {
          const value =
            typeof resp.data === 'number'
              ? resp.data
              : resp.data.total ?? resp.data.totalItems ?? undefined
          setTotalStockCount(value)
        }
      } catch (e) {
        console.error(e)
      }
    }

    fetchTotal()

    return () => {
      mounted = false
    }
  }, [stockFilters, search, refresh])

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      updateStockList(quantity, stockFilters, search)
    }, 300)

    return () => clearTimeout(timeoutId)
  }, [quantity, stockFilters, search, refresh])

  const normalizeStockItem = (item: any) => {
    const newItem = { ...item }
    if (newItem.receivedDate) {
      newItem.receivedDate = formatReceivedDate(newItem.receivedDate)
    }
    return {
      ...newItem,
      stockStatus: newItem.stockStatus ?? newItem.status ?? '',
      itemColorId: newItem.itemColorId ?? null,
      color: newItem.color ?? '',
      pantoneCode: newItem.pantoneCode ?? '',
      pantoneHex: newItem.pantoneHex ?? '',
    }
  }

  const handleNewModal = (item: any) => {
    setStock(normalizeStockItem(item))
    if (isFashionTenant) setStockMode('fashion')
    setModalActive(true)
    setModalSection('newModal')
  }

  const handleEdit = (item: any) => {
    setStock(normalizeStockItem(item))
    if (isFashionTenant) setStockMode('fashion')
    setItIsUpdate(true)
    setModalActive(true)
    setModalSection('firstCustomAdd')
  }

  const handleCreateVariation = (item: any) => {
    setStock({ ...normalizeStockItem(item), code: '', color: '' })
    if (isFashionTenant) setStockMode('fashion')
    setIsVariation(true)
    setItIsUpdate(true)
    setModalActive(true)
    setModalSection('firstCustomAdd')
  }

  const handleNewMovement = (item: any) => {
    setStock(normalizeStockItem(item))
    if (isFashionTenant) setStockMode('fashion')
    setItIsUpdate(true)
    setModalActive(true)
    setModalSection('modalEntryOrExit')
  }

  const handleSwitchActivation = async (item: any) => {
    try {
      await axios.put(
        `${process.env.REACT_APP_GATEWAY_BASE_URL}/backend/stock/activateStock`,
        {
          id: Number(item.id),
          isActivated: !item.isActivated,
        },
      )
      setFeedback({
        isOpen: true,
        message: `Item ${!item.isActivated ? 'ativado' : 'inativado'} com sucesso!`,
        statusOk: true,
      })
      updateStockList(quantity, stockFilters, search)
    } catch {
      setFeedback({
        isOpen: true,
        message: 'Erro ao mudar status de ativação.',
        statusOk: false,
      })
    }
  }

  const handleSelectRow = (id: string | number, selected: boolean) => {
    const numId = Number(id)
    setSelectedStockIds((prev) => {
      const next = new Set(prev)
      if (selected) {
        next.add(numId)
      } else {
        next.delete(numId)
      }
      return next
    })
  }

  const handleSelectAllRows = (selected: boolean) => {
    if (selected) {
      const allIds = stockList.map((item: any) => item.id)
      setSelectedStockIds(new Set(allIds))
    } else {
      setSelectedStockIds(new Set())
    }
  }

  const handleSaveBulkEdits = async () => {
    if (selectedStockIds.size === 0) return
    setIsBulkSaving(true)
    try {
      const idsToUpdate = Array.from(selectedStockIds)
      const activeEdits = Object.entries(bulkDraft).filter(([_, val]) => val !== undefined && val !== '')

      if (activeEdits.length === 0) {
        setFeedback({
          isOpen: true,
          message: 'Preencha ao menos uma alteração nos campos do cabeçalho antes de salvar.',
          statusOk: false,
        })
        setIsBulkSaving(false)
        return
      }

      let successCount = 0
      for (const id of idsToUpdate) {
        try {
          const payload: any = {}
          for (const [colId, val] of activeEdits) {
            if (colId === 'isActivated') {
              payload.isActivated = val === 'Ativo'
            } else if (colId === 'stockStatus') {
              payload.stockStatus = val
            } else {
              payload[colId] = val
            }
          }
          await ApiService.stock.update(id, payload)
          successCount++
        } catch (err) {
          console.error(`Erro ao atualizar estoque #${id} em massa:`, err)
        }
      }

      setFeedback({
        isOpen: true,
        message: `${successCount} estoque(s) atualizado(s) em massa com sucesso!`,
        statusOk: true,
      })
      setSelectedStockIds(new Set())
      setBulkDraft({})
      updateStockList(quantity, stockFilters, search)
    } catch (e) {
      console.error('Erro ao salvar em massa:', e)
      setFeedback({
        isOpen: true,
        message: 'Erro ao processar alteração em massa.',
        statusOk: false,
      })
    } finally {
      setIsBulkSaving(false)
    }
  }

  const fetchAllStockRows = async () => {
    const parameters: any = { size: 100000 }
    if (stockFilters && stockFilters.length > 0) parameters.filters = JSON.stringify(stockFilters)
    if (search && search.trim().length > 0) parameters.global = search
    const response = await ApiService.stock.findAll(parameters)
    return Array.isArray(response?.data) ? response.data : response?.data?.items || []
  }

  const handleSelectOption = (colId: string, option: any) => {
    const idField = `${colId}Id`
    if (!option) {
      setBulkDraft((prev) => {
        const next = { ...prev }
        delete next[colId]
        delete next[idField]
        return next
      })
      return
    }
    const name = option.name || option.type || option.heatNumber || String(option.id)
    setBulkDraft((prev) => ({
      ...prev,
      [colId]: name,
      ...(option.id ? { [idField]: option.id } : {}),
    }))
  }

  const renderBulkInput = (colId: string) => {
    const value = bulkDraft[colId] || ''
    const idValue = bulkDraft[`${colId}Id`]
    const selectedOption = value ? { id: idValue || value, name: value } : null

    if (colId === 'isActivated') {
      return (
        <select
          value={value}
          onChange={(e) => setBulkDraft((prev) => ({ ...prev, isActivated: e.target.value }))}
          style={{
            width: '100%',
            padding: '4px 6px',
            fontSize: '11px',
            borderRadius: '4px',
            border: '1px solid #0090d3',
            backgroundColor: '#fff',
            cursor: 'pointer',
          }}
        >
          <option value="">(Sem alteração)</option>
          <option value="Ativo">Ativo</option>
          <option value="Inativo">Inativo</option>
        </select>
      )
    }

    if (colId === 'certificateUrl' || colId === 'invoice' || colId === 'code' || colId === 'quantities' || colId === 'prices') {
      return null
    }

    const isLookup = [
      'material',
      'format',
      'finishing',
      'fabrication',
      'heatTreatment',
      'originCountry',
      'supplier',
      'stockStreet',
      'shelf',
      'stockLocation',
      'family',
      'stockStatus',
      'color',
      'heatNumber',
    ].includes(colId)

    if (isLookup) {
      return (
        <div style={{ minWidth: '130px', maxWidth: '200px' }}>
          <InfiniteSelect
            mode="crud"
            value={selectedOption}
            placeholder="Alterar..."
            searchPlaceholder="Buscar ou criar..."
            onSelect={(option) => handleSelectOption(colId, option)}
            showPantoneSwatch={false}
            fetchFunction={async (_limit, _offset, search) => {
              let response: any
              if (colId === 'material') {
                response = await ApiService.stockLookup.getMaterials({ type: 'STOCK' })
              } else if (colId === 'format') {
                response = await ApiService.stockLookup.getFormats()
              } else if (colId === 'finishing') {
                response = await ApiService.stockLookup.getFinishings()
              } else if (colId === 'fabrication') {
                response = await ApiService.stockLookup.getFabrications()
              } else if (colId === 'heatTreatment') {
                response = await ApiService.stockLookup.getHeatTreatments()
              } else if (colId === 'originCountry') {
                response = await ApiService.stockLookup.getOriginCountries()
              } else if (colId === 'supplier') {
                response = await ApiService.companies.findAll()
              } else if (colId === 'stockStreet') {
                response = await ApiService.stockLookup.getStockStreets()
              } else if (colId === 'shelf') {
                response = await ApiService.stockLookup.getShelfs()
              } else if (colId === 'stockLocation') {
                response = await ApiService.stockLookup.getStockLocations()
              } else if (colId === 'family') {
                response = await ApiService.stockLookup.getFamilies()
              } else if (colId === 'heatNumber') {
                response = await ApiService.stockLookup.getHeatNumbers()
              } else if (colId === 'color') {
                response = await ApiService.resources.getColors()
              } else if (colId === 'stockStatus') {
                response = await ApiService.stockLookup.getStockStatuses()
              }

              const items = Array.isArray(response?.data) ? response.data : Array.isArray(response) ? response : []
              const mapped = items
                .map((item: any, idx: number) => {
                  if (typeof item === 'string') return { id: item, name: item }
                  const name = item.name || item.type || item.ncm || item.status || item.heatNumber || ''
                  return {
                    id: item.id || name || `temp-${idx}`,
                    name: name,
                  }
                })
                .filter((item: any) => item.name && item.name.toLowerCase() !== 'null' && item.name.toLowerCase() !== 'undefined')

              return search
                ? mapped.filter((item: any) => item.name?.toLowerCase().includes(search.toLowerCase()))
                : mapped
            }}
            onItemCreate={async (name: string) => {
              let response: any
              const trimmedName = name.trim()
              if (colId === 'material') {
                response = await ApiService.stockLookup.createMaterial({ name: trimmedName, type: 'STOCK' })
              } else if (colId === 'format') {
                response = await ApiService.stockLookup.createFormat({ name: trimmedName })
              } else if (colId === 'finishing') {
                response = await ApiService.stockLookup.createFinishing({ name: trimmedName })
              } else if (colId === 'fabrication') {
                response = await ApiService.stockLookup.createFabrication({ name: trimmedName })
              } else if (colId === 'heatTreatment') {
                response = await ApiService.stockLookup.createHeatTreatment({ name: trimmedName })
              } else if (colId === 'originCountry') {
                response = await ApiService.stockLookup.createOriginCountry({ name: trimmedName })
              } else if (colId === 'stockStreet') {
                response = await ApiService.stockLookup.createStockStreet({ name: trimmedName })
              } else if (colId === 'shelf') {
                response = await ApiService.stockLookup.createShelf({ name: trimmedName })
              } else if (colId === 'stockLocation') {
                response = await ApiService.stockLookup.createStockLocation({ name: trimmedName })
              } else if (colId === 'family') {
                response = await ApiService.stockLookup.createFamily({ name: trimmedName })
              } else if (colId === 'heatNumber') {
                response = await ApiService.stockLookup.createHeatNumber({ name: trimmedName })
              } else if (colId === 'color') {
                response = await ApiService.resources.createColor({ name: trimmedName })
              } else if (colId === 'stockStatus') {
                response = await ApiService.stockLookup.createStockStatus({ name: trimmedName })
              }
              const resData = response?.data || {}
              return { id: resData.id || trimmedName, name: resData.name || resData.type || trimmedName }
            }}
            onItemDelete={async (id: any) => {
              if (colId === 'material') {
                await ApiService.stockLookup.deleteMaterial(id)
              } else if (colId === 'format') {
                await ApiService.stockLookup.deleteFormat(id)
              } else if (colId === 'finishing') {
                await ApiService.stockLookup.deleteFinishing(id)
              } else if (colId === 'fabrication') {
                await ApiService.stockLookup.deleteFabrication(id)
              } else if (colId === 'heatTreatment') {
                await ApiService.stockLookup.deleteHeatTreatment(id)
              } else if (colId === 'originCountry') {
                await ApiService.stockLookup.deleteOriginCountry(id)
              } else if (colId === 'stockStreet') {
                await ApiService.stockLookup.deleteStockStreet(id)
              } else if (colId === 'shelf') {
                await ApiService.stockLookup.deleteShelf(id)
              } else if (colId === 'stockLocation') {
                await ApiService.stockLookup.deleteStockLocation(id)
              } else if (colId === 'family') {
                await ApiService.stockLookup.deleteFamily(id)
              } else if (colId === 'heatNumber') {
                await ApiService.stockLookup.deleteHeatNumber(id)
              } else if (colId === 'color') {
                await ApiService.resources.deleteColor(id)
              } else if (colId === 'stockStatus') {
                await ApiService.status.deleteStatus(id)
              }
            }}
            allowCreate={true}
            allowDelete={true}
            showCreateOnlyIfNotExists={true}
            minCreateLength={2}
          />
        </div>
      )
    }

    return (
      <input
        type="text"
        placeholder="Alterar..."
        value={value}
        onChange={(e) => setBulkDraft((prev) => ({ ...prev, [colId]: e.target.value }))}
        style={{
          width: '100%',
          padding: '4px 6px',
          fontSize: '11px',
          borderRadius: '4px',
          border: '1px solid #0090d3',
          backgroundColor: '#fff',
          boxSizing: 'border-box',
        }}
      />
    )
  }

  const hiddenSet = new Set(columnsState.hidden ?? [])
  const visibleColumns = columns.filter((col) => !hiddenSet.has(col.id))

  const bulkHeaderRow = selectedStockIds.size > 0 ? (
    <tr style={{ backgroundColor: '#e0f2fe', borderBottom: '2px solid #0090d3' }}>
      <td style={{ width: '40px', padding: '4px', textAlign: 'center' }}>
        <span style={{ fontSize: '11px', fontWeight: 600, color: '#0090d3' }}>Lote</span>
      </td>
      {visibleColumns.map((col) => (
        <td key={col.id} style={{ padding: '4px 6px' }}>
          {renderBulkInput(col.id)}
        </td>
      ))}
      <td style={{ padding: '4px 6px' }} />
      <td style={{ padding: '4px' }} />
    </tr>
  ) : null

  const toolbarLeft = selectedStockIds.size > 0 ? (
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
      <span
        style={{
          backgroundColor: '#0090d3',
          color: '#fff',
          fontSize: '12px',
          fontWeight: 600,
          padding: '4px 12px',
          borderRadius: '16px',
        }}
      >
        {selectedStockIds.size} estoques selecionados
      </span>

      <button
        type="button"
        onClick={handleSaveBulkEdits}
        disabled={isBulkSaving}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          padding: '6px 14px',
          borderRadius: '6px',
          backgroundColor: '#10b981',
          color: '#fff',
          border: 'none',
          fontSize: '12px',
          fontWeight: 600,
          cursor: 'pointer',
        }}
      >
        <Check size={14} />
        {`Salvar ${selectedStockIds.size} Estoque(s) Editado(s)`}
      </button>

      <button
        type="button"
        onClick={() => {
          setSelectedStockIds(new Set())
          setBulkDraft({})
        }}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          padding: '6px 12px',
          borderRadius: '6px',
          backgroundColor: '#f3f4f6',
          color: '#374151',
          border: '1px solid #d1d5db',
          fontSize: '12px',
          fontWeight: 500,
          cursor: 'pointer',
        }}
      >
        <X size={14} />
        Limpar Seleção
      </button>
    </div>
  ) : null

  return (
    <>
      {isBulkSaving && <LoadingComponent />}
      <StandardDataTable
      columns={columns}
      data={stockList}
      columnsState={columnsState}
      onColumnsChange={handleColumnsChange}
      onRestoreColumns={handleRestoreColumns}
      totalCount={totalStockCount ?? null}
      onLoadMore={() => setQuantity((prev) => prev + 50)}
      hasMore={hasMoreStock}
      isLoading={isLoading}
      selectable
      selectedRowIds={selectedStockIds}
      onSelectRow={handleSelectRow}
      onSelectAllRows={handleSelectAllRows}
      bulkHeaderRow={bulkHeaderRow}
      toolbarLeft={toolbarLeft}
      exportFileName='estoque'
      exportSheetName='Estoque'
      exportRows={fetchAllStockRows}
      rowClassName={(item) => (item.isActivated === false ? 'yellowLine' : '')}
      rowActions={(item) => [
        { label: 'Visualizar', icon: <Eye size={16} />, onClick: () => handleNewModal(item) },
        {
          label: 'Editar',
          icon: <Pencil size={16} />,
          onClick: () => handleEdit(item),
          disabled: item.isActivated === false,
          title: item.isActivated === false ? 'Item inativo' : undefined,
        },
        {
          label: 'Criar Variação',
          icon: <Copy size={16} />,
          onClick: () => handleCreateVariation(item),
          disabled: item.isActivated === false,
          title: item.isActivated === false ? 'Item inativo' : undefined,
        },
        {
          label: 'Novo movimento',
          icon: <ArrowLeftRight size={16} />,
          onClick: () => handleNewMovement(item),
          disabled: item.isActivated === false,
          title: item.isActivated === false ? 'Item inativo' : undefined,
        },
        {
          label: item.isActivated === false ? 'Ativar' : 'Inativar',
          icon: <Power size={16} />,
          onClick: () => handleSwitchActivation(item),
        },
      ]}
    />
    </>
  )
}

export default StockTable
